import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Phone, Mail, MapPin, Globe, Repeat } from 'lucide-react';
import { useContactInterval } from '@/hooks/useContactInterval';
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings';
import {
  resolveCadenceDays,
  formatCadenceLabel,
  DEFAULT_CADENCE_OPTIONS,
} from '@/lib/contact-cadence';
import { useEmailComposer } from "@/components/email/EmailComposerProvider";
import { usePhoneCall } from '@/components/phone/PhoneCallProvider';
import { WeatherDisplay } from './WeatherDisplay';
import { supabase } from "@/lib/supabase";
import { formatPhoneNumber } from "@/lib/utils";

interface ContactHeaderProps {
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    job_title?: string;
    company_name?: string;
    primary_email?: string | null;
    primary_phone?: string;
    industry?: string;
    rank?: string;
    next_contact?: string | null;
  };
  onUpdate: () => void;
}

interface PhoneEntry {
  phone_number: string;
  isPrimary: boolean;
  label?: string;
}

interface Address {
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  postal_code?: string;
  country: string;
  lat?: string;
  long?: string;
  label?: string;
}

interface WebsiteDomain {
  id: number;
  website_domain: string;
  is_primary: boolean;
  label?: string;
}

export function ContactHeader({ contact, onUpdate: _onUpdate }: ContactHeaderProps) {
  const [phones, setPhones] = useState<PhoneEntry[]>([]);
  const [primaryEmail, setPrimaryEmail] = useState<{ email: string; label?: string } | null>(null);
  const [primaryWebsite, setPrimaryWebsite] = useState<WebsiteDomain | null>(null);
  const [primaryAddress, setPrimaryAddress] = useState<Address | null>(null);
  const { openComposer } = useEmailComposer();
  const { startCall } = usePhoneCall();
  // Track onUpdate reference changes to know when parent refetched

  // Refetch when contact.id changes or when parent refreshes contact data
  // (parent creates a new object reference on refetch, changing primary_email/primary_phone)
  useEffect(() => {
    // Reset state immediately to avoid showing stale data from previous contact
    setPhones([]);
    setPrimaryEmail(null);
    setPrimaryWebsite(null);
    setPrimaryAddress(null);

    const fetchContactDetails = async () => {
      try {
        const [phoneResult, emailResult, websiteResult, addressResult] = await Promise.all([
          supabase
            .from('phone_numbers')
            .select('phone_number, isPrimary, label')
            .eq('contact_id', contact.id)
            .order('isPrimary', { ascending: false }),
          supabase
            .from('email_address')
            .select('email, label')
            .eq('contact_id', contact.id)
            .eq('is_primary', true)
            .maybeSingle(),
          supabase
            .from('website_domains')
            .select('*')
            .eq('contact_id', contact.id)
            .eq('is_primary', true)
            .maybeSingle(),
          supabase
            .from('physical_addresses')
            .select('*')
            .eq('contact_id', contact.id)
            .eq('is_primary', true)
            .maybeSingle(),
        ]);

        if (phoneResult.error && phoneResult.error.code !== 'PGRST116') throw phoneResult.error;
        if (emailResult.error && emailResult.error.code !== 'PGRST116') throw emailResult.error;
        if (websiteResult.error && websiteResult.error.code !== 'PGRST116') throw websiteResult.error;
        if (addressResult.error && addressResult.error.code !== 'PGRST116') throw addressResult.error;

        setPhones(phoneResult.data || []);
        setPrimaryEmail(emailResult.data ? { email: emailResult.data.email, label: emailResult.data.label } : null);
        setPrimaryWebsite(websiteResult.data || null);
        setPrimaryAddress(addressResult.data || null);
      } catch (error) {
        console.error('Error fetching contact details:', error);
      }
    };

    fetchContactDetails();
  }, [contact]); // Re-fetch on ANY contact prop change (parent creates new object on refetch)

  const formatAddress = (address: Address): string => {
    const parts = [
      address.address_line1,
      address.address_line2,
      [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getGoogleMapsUrl = (address: Address): string => {
    const formattedAddress = formatAddress(address);
    const query = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const formatUrl = (domain: string): string => {
    return domain.startsWith('http') ? domain : `https://${domain}`;
  };

  const { intervalDays } = useContactInterval(contact.id);
  const { settings } = useWorkspaceSettings();
  const cadenceOptions =
    settings?.skip_options && Array.isArray(settings.skip_options) && settings.skip_options.length > 0
      ? settings.skip_options
      : DEFAULT_CADENCE_OPTIONS;
  // Renders "every 6 months", not "every 180 days" — the wording the user actually picked.
  const cadenceText = formatCadenceLabel(resolveCadenceDays(intervalDays), cadenceOptions);
  const nextContactDate = contact.next_contact ? new Date(contact.next_contact) : null;
  const isOverdue = nextContactDate !== null && nextContactDate < new Date();

  return (
    <div className="space-y-3">
      {/* Main Info Block */}
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 flex-shrink-0 border-2 border-background shadow-sm">
          <AvatarFallback className="text-lg font-semibold">
            {contact.first_name?.[0]}{contact.last_name?.[0]}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0 space-y-1">
          <h2 className="text-xl font-bold leading-tight">
            {contact.first_name} {contact.last_name}
          </h2>

          {contact.job_title && (
            <p className="text-sm text-muted-foreground leading-tight">
              {contact.job_title}
            </p>
          )}

          {contact.company_name && (
            <p className="text-sm font-medium leading-tight truncate">
              {contact.company_name}
            </p>
          )}
        </div>
      </div>

      {/* Contact Details Block */}
      <div className="space-y-2 pt-2 border-t">
        {/* All Phone Numbers */}
        {phones.map((phone, i) => (
          <a
            key={i}
            href={`tel:${phone.phone_number}`}
            onClick={(e) => {
              e.preventDefault();
              startCall(
                contact.id,
                phone.phone_number,
                `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
              );
              const telLink = document.createElement('a');
              telLink.href = `tel:${phone.phone_number}`;
              telLink.click();
            }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
            {...(i === 0 ? { 'data-tour-id': 'contact-detail.click-to-dial' } : {})}
          >
            <Phone className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            {formatPhoneNumber(phone.phone_number)}
            {phone.label && (
              <span className="text-xs text-muted-foreground/60">{phone.label}</span>
            )}
          </a>
        ))}

        {/* Primary Email Only */}
        {primaryEmail && (
          <button
            onClick={() => openComposer({ to: primaryEmail.email, contactId: contact.id })}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group w-full text-left"
          >
            <Mail className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            {primaryEmail.email}
            {primaryEmail.label && (
              <span className="text-xs text-muted-foreground/60">{primaryEmail.label}</span>
            )}
          </button>
        )}

        {/* Primary Website */}
        {primaryWebsite && (
          <a
            href={formatUrl(primaryWebsite.website_domain)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group max-w-full"
          >
            <Globe className="h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
            <span className="truncate">{primaryWebsite.website_domain}</span>
          </a>
        )}

        {/* Primary Address and Weather */}
        {primaryAddress && (
          <div className="space-y-1">
            <a
              href={getGoogleMapsUrl(primaryAddress)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
            >
              <MapPin className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors mt-0.5" />
              <span className="flex-1">
                {formatAddress(primaryAddress)}
                {primaryAddress.label && (
                  <span className="text-xs text-muted-foreground/60 ml-1">{primaryAddress.label}</span>
                )}
              </span>
            </a>

            {/* Weather Display */}
            {primaryAddress.lat && primaryAddress.long && (
              <div className="ml-6">
                <WeatherDisplay lat={primaryAddress.lat} long={primaryAddress.long} />
              </div>
            )}
          </div>
        )}

        {/* Next contact + cadence. Hidden entirely when there is no date: most contacts have
            never been called or skipped, and rendering the global default on all of them
            would be a row of noise on nearly every contact. */}
        {nextContactDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Repeat className="h-4 w-4 flex-shrink-0" />
            <span className={isOverdue ? 'text-amber-600 dark:text-amber-500' : undefined}>
              {/* Year included deliberately: with a 1-year cadence, "Next contact Jul 20"
                  is ambiguous about which July. */}
              Next contact {nextContactDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="text-xs text-muted-foreground/60">
              &middot; {cadenceText}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
